import { type Column } from '@tanstack/react-table';
import { type CSSProperties } from 'react';

import { cn } from '@/lib/utils';

import type { DataTableFeatures } from './dataTableFeatures';
import { type DataTableColumnMeta } from './types';

type DataTableSortAriaState = 'ascending' | 'descending' | 'none';

export type PinnedOffset = { left?: number; right?: number };

export type PinnedOffsets = Record<string, PinnedOffset>;

const getColumnMeta = <TData extends object>(column: Column<DataTableFeatures, TData>) =>
  column.columnDef.meta as DataTableColumnMeta | undefined;

const getColumnAlignment = <TData extends object>(column: Column<DataTableFeatures, TData>) =>
  getColumnMeta(column)?.align ?? 'start';

// Pinned column offsets are computed externally from the allocator's per-column widths
// rather than read from TanStack's `getStart`/`getAfter`. The TanStack memos for those
// helpers are keyed on `state.columnSizing` reference, which `useTable` swaps on
// every render via `setOptions`; the resulting reads can desync from the values React
// commits to the DOM. Threading a pre-computed offset map sidesteps that fragility.
export const getPinnedCellStyles = <TData extends object>(
  column: Column<DataTableFeatures, TData>,
  offsets: PinnedOffsets,
): CSSProperties => {
  const offset = offsets[column.id];
  if (offset == null) {
    return {};
  }

  return {
    left: offset.left,
    right: offset.right,
  };
};

export const getPinnedCellClassName = <TData extends object>(
  column: Column<DataTableFeatures, TData>,
  variant: 'header' | 'body' | 'footer',
  canScrollLeft: boolean,
  canScrollRight: boolean,
) => {
  const pinnedSide = column.getIsPinned();
  const isHeader = variant === 'header';
  const isFooter = variant === 'footer';

  return cn(
    pinnedSide && 'sticky',
    pinnedSide && (isFooter ? 'bg-gray-100' : 'bg-white group-data-[state=selected]:bg-gray-100'),
    pinnedSide && isHeader && 'z-40',
    pinnedSide && isFooter && 'z-30',
    pinnedSide && !isHeader && 'z-20',
    pinnedSide === 'start' &&
      column.getIsLastColumn('start') &&
      canScrollLeft && [
        'after:absolute after:inset-y-0 after:right-0 after:w-7.5',
        'after:translate-x-full after:transition-shadow after:duration-300',
        'after:pointer-events-none after:will-change-transform after:content-[""]',
        'after:[box-shadow:inset_10px_0_8px_-8px_rgba(5,5,5,0.06)]',
      ],
    pinnedSide === 'end' &&
      column.getIsFirstColumn('end') &&
      canScrollRight && [
        'before:absolute before:inset-y-0 before:left-0 before:w-7.5',
        'before:-translate-x-full before:transition-shadow before:duration-300',
        'before:pointer-events-none before:will-change-transform before:content-[""]',
        'before:[box-shadow:inset_-10px_0_8px_-8px_rgba(5,5,5,0.06)]',
      ],
  );
};

export const getColumnTextAlignmentClassName = <TData extends object>(
  column: Column<DataTableFeatures, TData>,
) => {
  const alignment = getColumnAlignment(column);

  if (alignment === 'end') {
    return 'text-end';
  }

  if (alignment === 'center') {
    return 'text-center';
  }

  return 'text-start';
};

export const getColumnHeaderJustificationClassName = <TData extends object>(
  column: Column<DataTableFeatures, TData>,
) => {
  const alignment = getColumnAlignment(column);

  if (alignment === 'end') {
    return 'justify-end';
  }

  if (alignment === 'center') {
    return 'justify-center';
  }

  return 'justify-start';
};

export const getAriaSortState = <TData extends object>(
  column: Column<DataTableFeatures, TData>,
): DataTableSortAriaState | undefined => {
  if (!column.getCanSort()) {
    return undefined;
  }

  const sorting = column.getIsSorted();

  if (sorting === 'asc') {
    return 'ascending';
  }

  if (sorting === 'desc') {
    return 'descending';
  }

  return 'none';
};
